import { removeTagFromCells } from "@/apis/table/route";
import {
  GetCellsListResponse,
  GetTagsFromCellResponse,
} from "@/apis/table/types";
import queries from "@/queries";
import { useMappingStore } from "@/stores/mapping";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { toast } from "sonner";
import { useWorkbookIdAndTableId } from "../../hook";
import { dropTagFromCells, dropTagFromTagInfos } from "./remove-tag-cache";

export interface RemoveTagFromCellsArgs {
  tagKey: string;
  roleKey: string;
  tagKoLabel: string;
}

interface RemoveTagFromCellsVariables extends RemoveTagFromCellsArgs {
  workbookId: string;
  tableId: string;
}

const REMOVE_TAG_FROM_CELLS_MUTATION_KEY = ["remove-tag-from-cells"];

/**
 * 트리의 태그 하나를 그 롤의 모든 셀에서 떼는 mutation.
 * 매핑 스토어에 의존하므로 매핑탭 안에서만 쓴다.
 */
export const useRemoveTagFromCells = () => {
  const {
    data: { workbookId, tableId },
  } = useWorkbookIdAndTableId();
  const queryClient = useQueryClient();
  const sheetId = useMappingStore((state) => state.fact.selected?.sheet?.id);
  const clearSheet = useMappingStore((state) => state.mappedInfos.clearSheet);

  const { mutate } = useMutation({
    mutationKey: REMOVE_TAG_FROM_CELLS_MUTATION_KEY,
    mutationFn: (variables: RemoveTagFromCellsVariables) =>
      removeTagFromCells(variables.workbookId, variables.tableId, {
        tagKey: variables.tagKey,
        roleKey: variables.roleKey,
      }),
    onSuccess: (response, variables) => {
      const { tagKey, tagKoLabel } = variables;
      const tableCtx = queries
        .workbook(variables.workbookId)
        ._ctx.tables(variables.tableId);
      const removedAddresses = new Set(response.cellAddresses);

      // 전체 선택 해제(workspace/index.tsx)와 같은 이유로 캐시를 먼저 손본다:
      // invalidate 의 refetch 가 끝나기 전에 셀이 선택되면 옛 태그가 store 에 심긴다.
      queryClient.setQueryData<GetCellsListResponse>(
        tableCtx._ctx.cells().queryKey,
        (oldData) =>
          oldData
            ? {
                ...oldData,
                cells: dropTagFromCells(oldData.cells, removedAddresses, tagKey),
              }
            : oldData,
      );
      // 셀별 태그 조회 캐시도 같은 레이스가 있다 — 방금 태그를 뗀 셀을 곧바로 클릭하면
      // 매핑 패널이 이 캐시로 옛 태그를 보여준다.
      queryClient.setQueriesData<GetTagsFromCellResponse>(
        {
          predicate: (query) => {
            const key = query.queryKey as unknown[];
            return (
              key.includes(variables.workbookId) &&
              key.includes(variables.tableId) &&
              key.includes("tagged") &&
              key.some(
                (part) => typeof part === "string" && removedAddresses.has(part),
              )
            );
          },
        },
        (oldData) =>
          oldData && Array.isArray(oldData.tags)
            ? { ...oldData, tags: dropTagFromTagInfos(oldData.tags, tagKey) }
            : oldData,
      );
      if (sheetId) {
        clearSheet(sheetId);
      }
      queryClient.invalidateQueries({ queryKey: tableCtx.queryKey });
      // 태그를 떼서 표가 비면 다음 액션 추천이 바뀔 수 있다(전체 선택 해제와 같은 이유).
      queryClient.invalidateQueries({
        queryKey: queries.workbook(variables.workbookId)._ctx.taggingGuidance()
          .queryKey,
      });

      toast.success(
        `'${tagKoLabel}' 태그가 ${response.cellAddresses.length}개 셀에서 제거되었습니다.`,
      );
    },
    onError: () => {
      toast.error("태그 제거에 실패했습니다.");
    },
  });

  const removeTag = useCallback(
    (args: RemoveTagFromCellsArgs) => {
      if (!workbookId || !tableId) {
        toast.error("표 정보를 찾지 못해 태그를 제거할 수 없습니다.");
        return;
      }
      // 확인 모달은 요청이 나가 있어도 다시 열 수 있다. 같은 요청이 겹치면 두 번째는 0개 제거로 끝난다.
      if (
        queryClient.isMutating({
          mutationKey: REMOVE_TAG_FROM_CELLS_MUTATION_KEY,
        }) > 0
      ) {
        toast.info("태그를 제거하는 중입니다. 잠시 후 다시 시도해 주세요.");
        return;
      }
      mutate({ ...args, workbookId, tableId });
    },
    [workbookId, tableId, queryClient, mutate],
  );

  return { removeTagFromCells: removeTag };
};
